/**
 * Opção individual de uma questão de múltipla escolha.
 */
export interface AlternativeOption {
  id?: number | null;
  /** Texto da alternativa — pode conter HTML */
  value?: string | null;
  /** Indica se é a alternativa correta (exibe destaque verde) */
  checked?: boolean | null;
}

/**
 * Lista de alternativas de questão de múltipla escolha.
 *
 * Cada alternativa exibe um badge circular com letra (A, B, C…) e destaca
 * a alternativa correta com fundo e borda esquerda em verde (base-success-500).
 *
 * @remarks Only suitable for read-only review contexts where `checked` indicates the correct answer.
 *
 * @example
 * ```tsx
 * <AlternativeList options={currentPreview.examTypeList ?? []} />
 * ```
 */
export interface AlternativeListProps {
  readonly options: readonly AlternativeOption[];
}

export function AlternativeList({ options }: AlternativeListProps) {
  return (
    <div className="overflow-hidden rounded-sm border-hairline border-ctx-layout-border">
      {options.map((opt, idx) => {
        const letter = String.fromCharCode(65 + idx);
        const isCorrect = opt.checked ?? false;

        return (
          <div key={idx}>
            <div
              className={[
                'flex items-center gap-micro px-mini py-micro',
                isCorrect
                  ? 'border-l-thick border-base-success-500 bg-base-success-100'
                  : 'bg-ctx-layout-surface',
              ].join(' ')}
            >
              <div
                className={[
                  'flex h-xs w-xs shrink-0 items-center justify-center rounded-pill ts-heading-xxs',
                  isCorrect
                    ? 'bg-base-success-500 text-ctx-content-titleAlternative'
                    : 'bg-ctx-layout-body text-ctx-content-base',
                ].join(' ')}
              >
                {letter}
              </div>
              <span
                className={
                  isCorrect
                    ? 'ts-subtitle-xs text-ctx-content-title dark:text-ctx-content-titleAlternative'
                    : 'ts-paragraph-xs text-ctx-content-base'
                }
                dangerouslySetInnerHTML={{ __html: opt.value ?? '' }}
              />
            </div>
            {idx !== options.length - 1 && <div className="h-px w-full bg-ctx-layout-border" />}
          </div>
        );
      })}
    </div>
  );
}
